import { computed, DestroyRef, effect, type ElementRef, inject, Injector, signal } from '@angular/core';

export function elementSize(fn: () => ElementRef<HTMLElement> | undefined, injector = inject(Injector)) {
  const destroyRef = injector.get(DestroyRef);

  const sizeSignal = signal({ width: 0, height: 0 });

  let resizeObserver: ResizeObserver | undefined;

  // ResizeObserver is not available in SSR
  if (typeof ResizeObserver !== 'undefined') {
    // We will only observe one element at the time, so we can assume that each emit contains a single entry
    // And therefore we don't have to initialize a new ResizeObserver on each elementRef signal change.
    resizeObserver = new ResizeObserver(([entry]) => {
      // To equal the offset{width,height} we have to use the `borderBoxSize`.
      // For simple HTML elements, there is only 1 (SVG, or CSS columns have multiple sizes).
      if (entry.borderBoxSize.length !== 0) {
        sizeSignal.set({
          width: entry.borderBoxSize[0].inlineSize,
          height: entry.borderBoxSize[0].blockSize,
        });
      }
    });

    destroyRef.onDestroy(() => resizeObserver?.disconnect());
  }

  effect(
    (onCleanup) => {
      const elRef = fn();
      const el = elRef?.nativeElement;
      if (!el) return;

      sizeSignal.set({ width: el.offsetWidth, height: el.offsetHeight });

      if (resizeObserver) {
        resizeObserver.observe(el);
        onCleanup(() => resizeObserver.unobserve(el));
      }
    },
    { injector },
  );

  return sizeSignal;
}

export function elementHeight(fn: () => ElementRef<HTMLElement> | undefined, injector = inject(Injector)) {
  const elementSizeSignal = elementSize(fn, injector);

  return computed(() => elementSizeSignal().height);
}

export function elementWidth(fn: () => ElementRef<HTMLElement> | undefined, injector = inject(Injector)) {
  const elementSizeSignal = elementSize(fn, injector);

  return computed(() => elementSizeSignal().width);
}
